import { DECK_X, WALL } from './porch';

/** the most pumpkins a porch holds, and how big one may be made */
export const MAX_PUMPKINS = 15, MAX_SCALE = 5.2, MIN_SCALE = 0.5;

/** footprints on the ground a pumpkin may not stand in: the two railings either side of the steps, posts included: x0, z0, x1, z1 */
export const RAILS: [number, number, number, number][] = [
  [-DECK_X + 0.08, -0.17, -0.85, -0.05],
  [0.85, -0.17, DECK_X - 0.08, -0.05],
];

export interface Disc { at: [number, number]; r: number }

/** pumpkins are not perfect circles; a touch of give so ribs and warts can kiss */
const GIVE = 0.94;

const pushRect = (p: [number, number], r: number, rc: [number, number, number, number], from: [number, number]): boolean => {
  const [x0, z0, x1, z1] = rc;
  const cx = Math.min(Math.max(p[0], x0), x1), cz = Math.min(Math.max(p[1], z0), z1);
  const dx = p[0] - cx, dz = p[1] - cz, d = Math.hypot(dx, dz);
  if (d >= r) return false;
  if (d > 1e-6) { p[0] = cx + (dx / d) * r; p[1] = cz + (dz / d) * r; return true; }
  // the middle is inside the rail: out by the side it came from
  p[1] = from[1] < (z0 + z1) / 2 ? z0 - r : z1 + r;
  return true;
};

const pushDisc = (p: [number, number], r: number, o: Disc): boolean => {
  const need = (r + o.r) * GIVE, dx = p[0] - o.at[0], dz = p[1] - o.at[1], d = Math.hypot(dx, dz);
  if (d >= need) return false;
  if (d < 1e-6) { p[0] = o.at[0] + need; return true; }
  p[0] = o.at[0] + (dx / d) * need; p[1] = o.at[1] + (dz / d) * need;
  return true;
};

/** is a pumpkin of this radius at this spot clear of the railings and every other pumpkin */
export function clear(at: [number, number], r: number, others: Disc[]): boolean {
  const p: [number, number] = [at[0], at[1]], e = r - 0.002;
  return !RAILS.some(rc => pushRect([p[0], p[1]], e, rc, at)) && !others.some(o => pushDisc([p[0], p[1]], e, { ...o, r: o.r - 0.002 / GIVE }));
}

/**
 * Where a pumpkin ends up when it is dragged from `from` to `to`: walked in small steps so a quick drag can't jump a
 * railing, pushed out of the rails and of the others each step. Grass, steps and deck are all open ground.
 */
export function settle(from: [number, number], to: [number, number], r: number, others: Disc[], bounds: (x: number, z: number) => [number, number]): [number, number] {
  const p: [number, number] = [from[0], from[1]], prev: [number, number] = [from[0], from[1]];
  // fixed small steps toward the target from wherever the last one ended, so a push-out is not undone by the next
  for (let s = 0; s < 400; s++) {
    const dx = to[0] - p[0], dz = to[1] - p[1], d = Math.hypot(dx, dz);
    if (d < 1e-4 && s > 0) break;
    const k = d < 1e-4 ? 0 : Math.min(1, 0.04 / d), q = bounds(p[0] + dx * k, p[1] + dz * k);
    p[0] = q[0]; p[1] = q[1];
    for (let it = 0; it < 4; it++) {
      let hit = false;
      for (const rc of RAILS) hit = pushRect(p, r, rc, prev) || hit;
      for (const o of others) hit = pushDisc(p, r, o) || hit;
      if (!hit) break;
    }
    // the push can land on the wall's side of the bounds
    const b = bounds(p[0], p[1]);
    p[0] = b[0]; p[1] = b[1];
    prev[0] = p[0]; prev[1] = p[1];
  }
  return [+p[0].toFixed(3), +p[1].toFixed(3)];
}


/**
 * Where a pumpkin let go at `at` comes to rest. A drag passes through the others; on letting go it slides to the
 * nearest clear spot it could reach without crossing a railing, searched in rings outward, the side it came from
 * first. With no room anywhere within `reach` it stays where it was let go, overlapping.
 */
export function rest(at: [number, number], r: number, others: Disc[], bounds: (x: number, z: number) => [number, number], from = at, reach = 2.5): [number, number] {
  if (clear(at, r, others)) return at;
  const step = 0.04;
  for (let d = step; d <= reach + 1e-9; d += step) {
    const n = Math.max(12, Math.ceil((Math.PI * 2 * d) / step)), ring: [number, number][] = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2, q: [number, number] = [at[0] + Math.cos(a) * d, at[1] + Math.sin(a) * d], b = bounds(q[0], q[1]);
      if (Math.hypot(b[0] - q[0], b[1] - q[1]) < 1e-6 && clear(q, r, others)) ring.push(q);
    }
    ring.sort((p, q) => Math.hypot(p[0] - from[0], p[1] - from[1]) - Math.hypot(q[0] - from[0], q[1] - from[1]));
    for (const q of ring) {
      const got = settle(at, q, r, [], bounds);
      if (Math.hypot(got[0] - q[0], got[1] - q[1]) < 0.01) return [+q[0].toFixed(3), +q[1].toFixed(3)];
    }
  }
  return at;
}

/**
 * Tidy spots for pumpkins of these radii, in the same order: the biggest first, towards the back and the middle of the
 * deck, the smaller ones in front of and between them with a little room each; the lawn only once the deck is full
 */
export function arrange(rs: number[]): [number, number][] {
  const n = rs.length, order = rs.map((r, i) => i).sort((a, b) => rs[b] - rs[a]);
  const out: [number, number][] = new Array(n), placed: Disc[] = [];
  const x1 = DECK_X - 0.2, z0 = WALL + 0.3;
  order.forEach((i, k) => {
    const r = rs[i], zt = n > 1 ? -1.7 + (k / (n - 1)) * 0.95 : -1.1;
    let best: [number, number] | null = null, score = Infinity;
    for (let zi = Math.ceil(z0 * 10); zi <= 40; zi++) {
      for (let xi = -Math.floor(x1 * 10); xi <= x1 * 10; xi++) {
        const x = xi / 10, z = zi / 10;
        if (Math.abs(x) > x1 - r * 0.5 || (z > -0.2 - r * 0.7 && z < 0.75 + r * 0.7)) continue;
        const s = Math.abs(z - zt) + 0.3 * Math.abs(x) + (z > 0 ? 3 + z : 0);
        if (s >= score || !clear([x, z], r + 0.04, []) || placed.some(o => Math.hypot(x - o.at[0], z - o.at[1]) < r + o.r + 0.06 + 0.1 * Math.min(r, o.r))) continue;
        best = [x, z]; score = s;
      }
    }
    out[i] = best ?? [0, 2];
    placed.push({ at: out[i], r: rs[i] });
  });
  return out;
}
